import { baseApi } from "@/shared/api/baseApi";
import type { MerchantHomeQuery } from "../merchantHomeApi";
import { workbenchCatalogSchema, workbenchLayoutResponseSchema, workbenchMutationResponseSchema, type WorkbenchMutation } from "./layoutSchemas";
import {
  parseWorkbenchContentResponse,
  merchantAdAccountBindingIDSchema,
  WORKBENCH_CONTENT_PAGE_LIMIT,
  type WorkbenchCardType,
  type WorkbenchContentResponse,
} from "./contentSchemas";

export type WorkbenchOwner = Pick<MerchantHomeQuery, "userId" | "organizationId">;
export type WorkbenchContentQuery = MerchantHomeQuery & { adAccountBindingId?: string; cardTypes?: WorkbenchCardType[]; cardType?: WorkbenchCardType; cursor?: string };
const path = ({ organizationId }: WorkbenchOwner) => `/v1/organizations/${encodeURIComponent(organizationId)}/merchant-home`;
export const workbenchContentRequest = (input: WorkbenchContentQuery) => {
  const adAccountBindingId = input.adAccountBindingId === undefined
    ? undefined
    : merchantAdAccountBindingIDSchema.parse(input.adAccountBindingId);
  return {
    url: `${path(input)}/card-content`,
    headers: { "X-Merchant-Home-Sync-Health": "v1" },
    params: {
      shop_binding_id: input.shopBindingId,
      ad_account_binding_id: adAccountBindingId,
      market: input.market,
      category: input.category,
      card_types: input.cardTypes?.join(","),
      card_type: input.cardType,
      cursor: input.cursor,
      limit: WORKBENCH_CONTENT_PAGE_LIMIT,
    },
  };
};
export const workbenchApi = baseApi.enhanceEndpoints({ addTagTypes: ["MerchantWorkbench"] }).injectEndpoints({ endpoints: builder => ({
  getWorkbenchCatalog: builder.query<ReturnType<typeof workbenchCatalogSchema.parse>, WorkbenchOwner>({ query: input => `${path(input)}/card-catalog`, transformResponse: response => workbenchCatalogSchema.parse(response) }),
  getWorkbenchLayout: builder.query<ReturnType<typeof workbenchLayoutResponseSchema.parse>, WorkbenchOwner>({ query: input => `${path(input)}/layout`, transformResponse: response => workbenchLayoutResponseSchema.parse(response), providesTags: (_data, _error, input) => [{ type: "MerchantWorkbench", id: `${input.userId}:${input.organizationId}` }] }),
  saveWorkbenchLayout: builder.mutation<ReturnType<typeof workbenchMutationResponseSchema.parse>, WorkbenchOwner & { mutation: WorkbenchMutation }>({ query: ({ mutation, ...owner }) => ({ url: `${path(owner)}/layout`, method: "PUT", body: mutation }), transformResponse: response => workbenchMutationResponseSchema.parse(response) }),
  getWorkbenchContent: builder.query<WorkbenchContentResponse, WorkbenchContentQuery>({
    query: workbenchContentRequest,
    transformResponse: (response, _meta, input) => parseWorkbenchContentResponse(response, input),
    keepUnusedDataFor: 0,
    providesTags: (_data, _error, input) => [
      "MerchantStoreBindings",
      { type: "MerchantAdAccountBindings", id: input.organizationId },
    ],
  }),
}) });
export const { useGetWorkbenchCatalogQuery, useGetWorkbenchLayoutQuery, useSaveWorkbenchLayoutMutation, useLazyGetWorkbenchLayoutQuery, useGetWorkbenchContentQuery, useLazyGetWorkbenchContentQuery } = workbenchApi;
